import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";

import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";

import { formatDateTime } from "../../lib/format";
import { RISK_REPORTS, useForexRisk } from "./api";
import {
  FilterBar,
  FilterField,
  ACCOUNT_COLUMNS,
  AccountCells,
  Money,
  PAGE_SIZE_OPTIONS,
  PagePager,
  PartialTag,
  orDash,
} from "./parts";

const EMPTY = { login: "", username: "", from: "", to: "", pageSize: "50" };

// Only profit and closed-trades take a date: a position opened last year is
// still risk today, so the other two answer a date filter with a 400.
const DATED = ["profit", "closed-trades"];

export const COLUMNS = {
  profit: [
    ...ACCOUNT_COLUMNS,
    { label: "Trades", align: "right" },
    { label: "Profit", align: "right" },
  ],
  "closed-trades": [
    { label: "Closed" },
    { label: "Position" },
    ...ACCOUNT_COLUMNS,
    { label: "Symbol" },
    { label: "Type" },
    { label: "Volume", align: "right" },
    { label: "Volume total", align: "right" },
    { label: "Open price", align: "right" },
    { label: "Close price", align: "right" },
    { label: "Opened" },
    { label: "Profit", align: "right" },
  ],
  "open-positions": [
    { label: "Opened" },
    { label: "Position" },
    ...ACCOUNT_COLUMNS,
    { label: "Symbol" },
    { label: "Type" },
    { label: "Volume", align: "right" },
    { label: "Open price", align: "right" },
    { label: "Current price", align: "right" },
    { label: "SL", align: "right" },
    { label: "TP", align: "right" },
    { label: "Profit", align: "right" },
    { label: "Storage", align: "right" },
  ],
  "pending-orders": [
    { label: "Placed" },
    { label: "Order" },
    ...ACCOUNT_COLUMNS,
    { label: "Symbol" },
    { label: "Type" },
    { label: "Volume", align: "right" },
    { label: "Price", align: "right" },
    { label: "SL", align: "right" },
    { label: "TP", align: "right" },
    { label: "Expires" },
  ],
};

// datetime-local is the admin's local time; the API wants UTC.
const toUtc = (v) => (v ? new Date(v).toISOString() : "");

const mono = "whitespace-nowrap font-mono";
const muted = "whitespace-nowrap font-mono text-muted-foreground";

export function renderRow(report, row) {
  switch (report) {
    case "profit":
      return (
        <tr key={row.login} className="hover:bg-white/[0.02]">
          <AccountCells row={row} />
          <Td align="right" className={mono}>{row.trades}</Td>
          <Td align="right"><Money value={row.profit} /></Td>
        </tr>
      );
    case "closed-trades":
      return (
        <tr key={`${row.position}-${row.closeTime}`} className="hover:bg-white/[0.02]">
          <Td className={muted}>{formatDateTime(row.closeTime)}</Td>
          <Td className={mono}>{row.position}</Td>
          <AccountCells row={row} />
          <Td className={mono}>{row.symbol}</Td>
          <Td className="text-xs">{row.type}</Td>
          {/* Different volumes = closed in part; the rest is still open. */}
          <Td align="right" className={mono}>
            {row.volume}
            {row.volume !== row.volumeTotal && <PartialTag />}
          </Td>
          <Td align="right" className={mono}>{row.volumeTotal}</Td>
          <Td align="right" className={mono}>{row.openPrice}</Td>
          <Td align="right" className={mono}>{row.closePrice}</Td>
          <Td className={muted}>{formatDateTime(row.openTime)}</Td>
          <Td align="right"><Money value={row.profit} /></Td>
        </tr>
      );
    case "open-positions":
      return (
        <tr key={row.position} className="hover:bg-white/[0.02]">
          <Td className={muted}>{formatDateTime(row.openTime)}</Td>
          <Td className={mono}>{row.position}</Td>
          <AccountCells row={row} />
          <Td className={mono}>{row.symbol}</Td>
          <Td className="text-xs">{row.type}</Td>
          <Td align="right" className={mono}>{row.volume}</Td>
          <Td align="right" className={mono}>{row.openPrice}</Td>
          <Td align="right" className={mono}>{row.currentPrice}</Td>
          <Td align="right" className={mono}>{orDash(row.priceSL)}</Td>
          <Td align="right" className={mono}>{orDash(row.priceTP)}</Td>
          {/* Market move and financing cost answer different questions: never summed. */}
          <Td align="right"><Money value={row.profit} /></Td>
          <Td align="right"><Money value={row.storage} /></Td>
        </tr>
      );
    default:
      return (
        <tr key={row.order} className="hover:bg-white/[0.02]">
          <Td className={muted}>{formatDateTime(row.setupTime)}</Td>
          <Td className={mono}>{row.order}</Td>
          <AccountCells row={row} />
          <Td className={mono}>{row.symbol}</Td>
          <Td className="whitespace-nowrap text-xs">{row.type}</Td>
          <Td align="right" className={mono}>{row.volume}</Td>
          <Td align="right" className={mono}>{row.priceOrder}</Td>
          <Td align="right" className={mono}>{orDash(row.priceSL)}</Td>
          <Td align="right" className={mono}>{orDash(row.priceTP)}</Td>
          <Td className={muted}>
            {row.expiresAt ? formatDateTime(row.expiresAt) : "No expiry"}
          </Td>
        </tr>
      );
  }
}

export default function RiskReports() {
  const [report, setReport] = useState("profit");
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  // closed-trades pages by cursor; the stack is what makes Prev possible.
  const [cursors, setCursors] = useState([""]);

  const dated = DATED.includes(report);
  const params = {
    login: applied.login.trim(),
    username: applied.username.trim(),
    ...(dated ? { from: toUtc(applied.from), to: toUtc(applied.to) } : {}),
    ...(report !== "profit" ? { pageSize: applied.pageSize } : {}),
    ...(report === "closed-trades" ? { cursor: cursors[cursors.length - 1] } : {}),
    ...(!dated ? { page } : {}),
  };

  const q = useForexRisk(report, params);
  const data = q.data;

  const resetPaging = () => {
    setPage(1);
    setCursors([""]);
  };

  const switchReport = (r) => {
    setReport(r);
    resetPaging();
  };

  const apply = () => {
    setApplied(draft);
    resetPaging();
  };

  const reset = () => {
    setDraft(EMPTY);
    setApplied(EMPTY);
    resetPaging();
  };

  return (
    <QueueLayout
      eyebrow="Forex"
      title="Risk"
      accent="reports"
      description="What the live book is carrying: closed trades, realised profit, open positions and orders waiting to fill. Real-money accounts only."
      statusOptions={RISK_REPORTS}
      status={report}
      onStatus={switchReport}
      isFetching={q.isFetching}
      onRefresh={() => q.refetch()}
    >
      <FilterBar onApply={apply} onReset={reset}>
        <FilterField label="MT5 login(s)" name="login" draft={draft} setDraft={setDraft} placeholder="101895, 510804" />
        <FilterField label="Username(s)" name="username" draft={draft} setDraft={setDraft} placeholder="Skips unattributed accounts" />
        {dated && (
          <>
            <FilterField label="Closed from" name="from" type="datetime-local" draft={draft} setDraft={setDraft} />
            <FilterField label="Closed to" name="to" type="datetime-local" draft={draft} setDraft={setDraft} />
          </>
        )}
        {report !== "profit" && (
          <FilterField label="Page size" name="pageSize" options={PAGE_SIZE_OPTIONS} draft={draft} setDraft={setDraft} />
        )}
      </FilterBar>

      {report === "profit" && data && (
        <div className="flex gap-8 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Accounts</div>
            <div className="mt-1 font-mono text-lg">{data.accounts}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Total profit</div>
            <div className="mt-1 text-lg"><Money value={data.total} /></div>
          </div>
        </div>
      )}

      {/* A 503 is never an empty list: it stays an error state. */}
      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <QueueTable
            columns={COLUMNS[report]}
            isLoading={q.isLoading}
            rows={data?.data}
            renderRow={(row) => renderRow(report, row)}
          />

          {report === "closed-trades" && data && (
            // No total count by design; hasMore is the only end marker.
            <div className="flex items-center justify-end gap-1.5 px-5 py-3 text-xs text-muted-foreground">
              <span className="mr-2 font-mono">Page {cursors.length}</span>
              <Button
                variant="outline"
                size="icon"
                disabled={cursors.length <= 1}
                onClick={() => setCursors((c) => c.slice(0, -1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                disabled={!data.hasMore}
                onClick={() => setCursors((c) => [...c, data.nextCursor])}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          {!dated && <PagePager data={data} onChange={setPage} />}
        </>
      )}
    </QueueLayout>
  );
}
